import React, { useState } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Typography,
  Avatar,
  Tooltip,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  EyeOutlined,
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  MailOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useGetConsultantApplicationsQuery } from "../../../redux/rtk/apiRtk";
import { primaryColor } from "../../../utils/constants";

const { Title, Text } = Typography;
const { Option } = Select;

export const ConsultantApplications = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");

  const {
    data: applicationsData,
    isLoading,
    refetch,
    error,
  } = useGetConsultantApplicationsQuery({
    status: statusFilter === "all" ? "" : statusFilter,
    page: currentPage,
    limit: pageSize,
  });

  const applications = applicationsData?.applications || [];
  const totalCount = applicationsData?.pagination?.totalItems || 0;
  const stats = applicationsData?.stats || {};

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "orange";
      case "approved":
        return "green";
      case "rejected":
        return "red";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <ClockCircleOutlined />;
      case "approved":
        return <CheckCircleOutlined />;
      case "rejected":
        return <CloseCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const columns = [
    {
      title: "Applicant",
      key: "applicant",
      render: (_, record) => (
        <div className="flex items-center">
          <Avatar
            size={40}
            src={record.image}
            icon={<UserOutlined />}
            className="mr-3"
          />
          <div>
            <Text strong className="block">
              {record.firstName} {record.lastName}
            </Text>
            <Text type="secondary" className="text-sm">
              {Array.isArray(record.category)
                ? record.category.join(", ")
                : record.category}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, record) => (
        <div>
          <div className="flex items-center mb-1">
            <MailOutlined className="secondary " />
            <Text className="ml-2">{record?.user?.email}</Text>
          </div>
        </div>
      ),
    },
    {
      title: "Price",
      dataIndex: "pricePerMinute",
      key: "pricePerMinute",
      render: (price) => (
        <Text strong style={{ color: primaryColor }}>
          ₹{price}/min
        </Text>
      ),
    },
    {
      title: "Applied On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (
        <div className="flex items-center">
          <CalendarOutlined className="mr-2 text-gray-400" />
          <Text>{new Date(date).toLocaleDateString()}</Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={getStatusColor(status)}
          icon={getStatusIcon(status)}
          className="capitalize"
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="primary"
              ghost
              icon={<EyeOutlined />}
              onClick={() => navigate(`/consultant-applications/${record._id}`)}
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              View
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filteredApplications = applications.filter((app) =>
    searchText
      ? `${app.firstName} ${app.lastName} ${app.email} ${app.phone}`
          .toLowerCase()
          .includes(searchText.toLowerCase())
      : true
  );

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-6">
          <Title level={2} style={{ margin: 0, color: "#1a1a1a" }}>
            Consultant Applications
          </Title>
          <Text type="secondary" className="text-base">
            Review and manage consultant applications
          </Text>
        </div>

        {/* Stats Cards */}
        <Row gutter={[24, 24]} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card className="text-center">
              <Statistic
                title="Total Applications"
                value={stats.total || 0}
                valueStyle={{ color: primaryColor }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="text-center">
              <Statistic
                title="Pending"
                value={stats.pending || 0}
                valueStyle={{ color: "#faad14" }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="text-center">
              <Statistic
                title="Approved"
                value={stats.approved || 0}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="text-center">
              <Statistic
                title="Rejected"
                value={stats.rejected || 0}
                valueStyle={{ color: "#ff4d4f" }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-6">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search applications..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by status"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: "100%" }}
                suffixIcon={<FilterOutlined />}
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="approved">Approved</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
            </Col>
            <Col xs={24} sm={24} md={10}>
              <div className="flex justify-end">
                <Button onClick={refetch} loading={isLoading}>
                  Refresh
                </Button>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Applications Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredApplications}
            rowKey="_id"
            loading={isLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalCount,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} applications`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            scroll={{ x: 800 }}
            className="custom-table"
          />
        </Card>
      </motion.div>

      <style jsx="true">{`
        .custom-table .ant-table-thead > tr > th {
          background-color: ${primaryColor}08;
          border-bottom: 2px solid ${primaryColor}20;
          font-weight: 600;
        }

        .custom-table .ant-table-tbody > tr:hover > td {
          background-color: ${primaryColor}05;
        }

        .ant-statistic-content-value {
          font-size: 24px !important;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};
